import { ElectronApi } from '@/services/electron.api';
import { emptyDir, ensureDir, testAccess } from './fs/fs-extra.utils';
import { dataURItoBlob, writeImage } from './fs/image.utils';
import { RemoteLibsService } from '@/services/remote-libs.service';
import { getRoundRectangle, roundImage } from './canvas/canvas.utils';

const _saveFrameImages = async (toDir: string, frameURIs: string[]) => {
  const { fs, path } = ElectronApi;
  if (!fs || !path) return;
  const dir = toDir;
  await ensureDir(dir);
  await emptyDir(dir);
  for (let i = 0; i < frameURIs.length; i++) {
    const filename = path.join(dir, 'frame_' + i + '.webp');
    const frameURI = frameURIs[i];
    const blob = dataURItoBlob(frameURI);
    const arrBf = await blob.arrayBuffer();
    await fs.promises.writeFile(filename, new Uint8Array(arrBf));
  }
};

const _framesToWebp = async (frameDir: string, webp: string, fps: number, useAlpha = false) => {
  const { path } = ElectronApi;
  let ffmpeg = await new RemoteLibsService().getLibFilename('ffmpeg');
  // Linux回退：远程库不可用时使用系统ffmpeg
  if (!ffmpeg && (await testAccess('/usr/bin/ffmpeg'))) {
    ffmpeg = '/usr/bin/ffmpeg';
  }
  if (!ffmpeg || !path) return;
  const args = [
    '-f',
    'image2',
    '-r',
    String(fps),
    '-i',
    path.join(frameDir, 'frame_%d.webp'),
    '-r',
    '30',
    '-vcodec',
    'libwebp',
    ...(useAlpha ? ['-pix_fmt', 'yuva420p'] : []),
    '-loop',
    '0',
    '-y',
    webp
  ];
  await ElectronApi.execFile(ffmpeg, args);
};

const _webmToWebp = async (data: {
  webm: string;
  webp: string;
  framesDir: string;
  radius: number;
}) => {
  const { webm, webp, framesDir, radius } = data;

  const { path, fs } = ElectronApi;
  const ffmpeg = await new RemoteLibsService().getLibFilename('ffmpeg');
  if (!ffmpeg || !path || !fs) return;
  // ffmpeg -f image2 -r 60 -i frame_%d.png -vcodec libwebp -loop 0  -y output.webp
  // ffmpeg -f image2 -r 60 -i frame_%d.png -r 20 -vcodec libwebp -loop 0  -y output.webp

  // 将webm拆分为图片
  await ElectronApi.execFile(ffmpeg, [
    '-i',
    webm,
    // 这里的r指定1秒使用多少张图片去合并
    '-vf',
    'fps=60',
    path.join(framesDir, 'frame_%d.png')
  ]);

  // 依次将图片切圆角
  const frameFiles = await fs.promises
    .readdir(framesDir)
    .then((r) => r.filter((r) => /^frame_\d+\.png$/.test(r)));
  for (let i = 0; i < frameFiles.length; i++) {
    const _frameFile = frameFiles[i];
    const frameFile = path.join(framesDir, _frameFile);
    const rounded = await roundImage({
      imageFilename: frameFile,
      radius,
      type: 'image/webp'
    });
    await writeImage(frameFile.replace(/\.png$/, '.webp'), rounded);
    // console.log(rounded
  }

  // 将图片转为webp动图
  // await ElectronApi.execFile(ffmpeg, [
  //   '-framerate',
  //   '60',
  //   '-i',
  //   path.join(framesDir, 'frame_%d.png'),
  //   '-loop',
  //   '0',
  //   '-qscale',
  //   '80',
  //   webp
  // ]);
  await ElectronApi.execFile(ffmpeg, [
    '-f',
    'image2',
    // 这里的r指定1秒使用多少张图片去合并
    '-r',
    String(60),
    '-i',
    path.join(framesDir, 'frame_%d.webp'),
    // 这里的r指定输出帧率
    '-r',
    '30',
    '-vcodec',
    'libwebp',
    '-loop',
    '0',
    '-y',
    webp
  ]);
};

/**
 * 通过帧数据方式导出webp
 * @returns
 */
export const frameImagesToAnimateWebp = async (data: {
  frameURIs: string[];
  duration: number;
  useAlpha?: boolean;
}) => {
  const { frameURIs, duration, useAlpha = false } = data;

  const { fs, path } = ElectronApi;
  if (!fs || !path) return;

  const tempDir = await ElectronApi.getPath('temp');
  const frameDir = path.join(tempDir, '_frames');
  await emptyDir(frameDir);

  const webp = path.join(tempDir, '动态预览图.webp');
  if (await testAccess(webp)) await fs.promises.rm(webp);

  // 将序列帧图片保存到目录中
  await _saveFrameImages(frameDir, frameURIs);

  // 将序列帧转为webp
  // 获取帧率 根据总体播放时长和序列帧图片数量确定
  const fps = Math.floor(frameURIs.length / (duration / 1000));
  console.log('动画播放时长：', duration + 'ms', '帧率：', fps);

  try {
    await _framesToWebp(frameDir, webp, fps, useAlpha);
  } catch (e) {
    console.warn('[ffmpeg.webp] _framesToWebp failed:', e);
  }

  // 检查webp文件是否成功生成（ffmpeg可能因不支持alpha等原因失败）
  if (!(await testAccess(webp))) {
    if (!import.meta.env.DEV) {
      await emptyDir(frameDir);
      await fs.promises.rmdir(frameDir);
    }
    return undefined;
  }

  // 删除系列帧目录
  if (!import.meta.env.DEV) {
    await emptyDir(frameDir);
    await fs.promises.rmdir(frameDir);
  }
  return webp;
};

export const mediaBlobToAnimateWebp = async (data: {
  webmBlob: Blob;
  width: number;
  height: number;
  radius: number;
}) => {
  const { webmBlob, radius } = data;

  const { fs, path } = ElectronApi;
  if (!fs || !path) return;

  const tempDir = await ElectronApi.getPath('temp');
  const frameDir = path.join(tempDir, '_frames');
  await ensureDir(frameDir);
  await emptyDir(frameDir);

  const webm = path.join(tempDir, '动态预览图.webm');
  const webp = path.join(tempDir, '动态预览图.webp');
  if (await testAccess(webp)) await fs.promises.rm(webp);

  const arrBf = await webmBlob.arrayBuffer();
  await fs.promises.writeFile(webm, new Uint8Array(arrBf));

  // // 创建遮罩图
  // const maskName = path.join(frameDir, 'mask.png');
  // await writeImage(maskName, data.maskDataURI);

  await _webmToWebp({ webm, webp, framesDir: frameDir, radius });

  // 删除系列帧目录
  if (!import.meta.env.DEV) {
    await emptyDir(frameDir);
    await fs.promises.rmdir(frameDir);
  }

  return webp;
};
